// Org tenancy — client half. Memberships at orgs/{orgId}/members/{uid} are
// the source of truth (see backend/shared/entities/registry.js); this module
// provides the "my organizations" listing, the two bootstrap paths (create an
// org / claim a seeded org by ownerEmail), invites, and a current-org context.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import firebase, { db } from '../firebaseConfig';
import { useAuth } from '../AuthContext';
import { useToast } from '../components/Toast';

const CURRENT_ORG_KEY = 'fastlegal_current_org';

// All orgs I belong to (live, via collection-group query on members.uid).
export function useMyMemberships() {
  const { user } = useAuth();
  const [memberships, setMemberships] = useState(null);

  useEffect(() => {
    if (!user || user.isAnonymous) {
      setMemberships(null);
      return undefined;
    }
    const unsub = db
      .collectionGroup('members')
      .where('uid', '==', user.uid)
      .onSnapshot(
        (snap) =>
          setMemberships(
            snap.docs.map((d) => ({ orgId: d.ref.parent.parent.id, ...d.data() }))
          ),
        () => setMemberships([])
      );
    return () => unsub();
  }, [user]);

  return memberships; // null = loading
}

// Seeded orgs claimable by my email (org.ownerEmail == my email, no
// membership yet). The generated rules allow exactly this filtered read.
export function useClaimableOrgs(memberships) {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState([]);

  useEffect(() => {
    if (!user?.email || memberships === null) return undefined;
    const unsub = db
      .collection('orgs')
      .where('ownerEmail', '==', user.email)
      .onSnapshot(
        (snap) => {
          const mine = new Set((memberships || []).map((m) => m.orgId));
          setOrgs(snap.docs.filter((d) => !mine.has(d.id)).map((d) => ({ id: d.id, ...d.data() })));
        },
        () => setOrgs([])
      );
    return () => unsub();
  }, [user, memberships]);

  return orgs;
}

// Pending invites addressed to my email.
export function useMyInvites() {
  const { user } = useAuth();
  const [invites, setInvites] = useState([]);

  useEffect(() => {
    if (!user?.email) return undefined;
    const unsub = db
      .collection('invites')
      .where('email', '==', user.email.toLowerCase())
      .where('status', '==', 'pending')
      .onSnapshot(
        (snap) => setInvites(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
        () => setInvites([])
      );
    return () => unsub();
  }, [user]);

  return invites;
}

// Claim a seeded org: write my own owner membership (rules verify ownerEmail).
export async function claimOrg(org, user) {
  await db.doc(`orgs/${org.id}/members/${user.uid}`).set({
    uid: user.uid,
    email: user.email,
    displayName: user.displayName || user.email.split('@')[0],
    role: 'owner',
    addedBy: user.uid,
    createdAt: Date.now(),
  });
}

// Create an org + my owner membership in one batch (getAfter bootstrap rule).
export async function createOrg(name, user) {
  const batch = db.batch();
  const orgRef = db.collection('orgs').doc();
  batch.set(orgRef, {
    name,
    description: '',
    ownerUid: user.uid,
    ownerEmail: user.email || '',
    createdAt: Date.now(),
  });
  batch.set(db.doc(`orgs/${orgRef.id}/members/${user.uid}`), {
    uid: user.uid,
    email: user.email || '',
    displayName: user.displayName || (user.email || '').split('@')[0],
    role: 'owner',
    addedBy: user.uid,
    createdAt: Date.now(),
  });
  await batch.commit();
  return orgRef.id;
}

// Accept an invite (rules allow the matching email); the backend claims
// worker then creates the membership.
export async function acceptInvite(invite, user) {
  await db.doc(`invites/${invite.id}`).set(
    { status: 'accepted', acceptedByUid: user.uid },
    { merge: true }
  );
}

// `projects` (optional): [{ dealId, dealName, dealRole, powers?, partyKey? }]
// — provisioned by the claims worker when the invite is accepted, so the
// user lands with exactly the intended project access.
export async function createInvite(org, email, role, user, projects = []) {
  // `org` may be the org doc or a bare org id — the org snapshot can still
  // be loading right after an org is created, and `null.id` here was the
  // first thing a brand-new owner hit when inviting a colleague.
  const orgId = typeof org === 'string' ? org : org?.id;
  if (!orgId) throw new Error('Organization not loaded yet — give it a second and try again');
  const orgName = typeof org === 'string' ? '' : (org?.name || '');
  await db.collection('invites').add({
    orgId,
    orgName,
    email: email.trim().toLowerCase(),
    role,
    projects,
    status: 'pending',
    invitedBy: user.uid,
    createdAt: Date.now(),
    expiresAt: Date.now() + 14 * 24 * 3600 * 1000,
  });
}

// ---- Current-org context ---------------------------------------------------------
const OrgContext = createContext({ org: null, orgId: null, role: null, memberships: null, setOrgId: () => {} });

export function OrgProvider({ children }) {
  const memberships = useMyMemberships();
  const [orgId, setOrgIdState] = useState(() => localStorage.getItem(CURRENT_ORG_KEY) || null);
  const [org, setOrg] = useState(null);

  // Auto-select: stored org if still a member, else the first membership.
  useEffect(() => {
    if (memberships === null) return;
    const valid = memberships.some((m) => m.orgId === orgId);
    if (!valid) {
      const next = memberships[0]?.orgId || null;
      setOrgIdState(next);
      if (next) localStorage.setItem(CURRENT_ORG_KEY, next);
    }
  }, [memberships, orgId]);

  useEffect(() => {
    if (!orgId) {
      setOrg(null);
      return undefined;
    }
    // Right after createOrg, the memberships listener fires from the LOCAL
    // pending write and selects the new org before the server has committed
    // the batch; the first listen on orgs/{id} is then denied by rules (no
    // member doc server-side yet) — and a denied Firestore listener never
    // recovers on its own. Re-subscribe with backoff instead of going dark
    // until a reload (this is what broke the first customer's first invite).
    let unsub = null;
    let timer = null;
    let attempt = 0;
    let cancelled = false;
    const subscribe = () => {
      unsub = db.doc(`orgs/${orgId}`).onSnapshot(
        (snap) => { attempt = 0; setOrg(snap.exists ? { id: snap.id, ...snap.data() } : null); },
        () => {
          setOrg(null);
          if (cancelled || attempt >= 6) return;
          attempt += 1;
          timer = setTimeout(subscribe, Math.min(500 * 2 ** (attempt - 1), 8000));
        }
      );
    };
    subscribe();
    return () => { cancelled = true; clearTimeout(timer); unsub?.(); };
  }, [orgId]);

  const value = useMemo(() => {
    const role = (memberships || []).find((m) => m.orgId === orgId)?.role || null;
    return {
      org,
      orgId,
      role,
      memberships,
      setOrgId: (id) => {
        setOrgIdState(id);
        if (id) localStorage.setItem(CURRENT_ORG_KEY, id);
      },
    };
  }, [org, orgId, memberships]);

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export const useOrg = () => useContext(OrgContext);

// Pending invites are accepted for every signed-in user, not only someone
// with zero orgs. The lobby used to be the only place that flipped
// status→accepted, so a person who already had an account clicked Accept,
// signed in, and nothing happened. A failure used to be swallowed into a
// permanent "Joining…" screen.
const InviteJoinContext = createContext({
  joiningName: null,
  acceptError: null,
  accept: async () => {},
});

export function InviteAcceptor({ children }) {
  const { user } = useAuth();
  const { memberships, setOrgId } = useOrg();
  const invites = useMyInvites();
  const toast = useToast();
  const [joiningName, setJoiningName] = useState(null);
  const [acceptError, setAcceptError] = useState(null);
  const inflight = useRef(new Set());
  const pendingOrgs = useRef(new Map());
  const membershipsRef = useRef(memberships);
  const setOrgIdRef = useRef(setOrgId);
  membershipsRef.current = memberships;
  setOrgIdRef.current = setOrgId;

  const claimReady = useCallback(() => {
    const ms = membershipsRef.current;
    if (!ms) return;
    // An invite link carries ?invited=1. Follow it into that org. Otherwise
    // leave someone who already works in an org where they were — the
    // membership still exists and the account menu can switch.
    const fromInviteLink = new URLSearchParams(window.location.search).get('invited') === '1';
    for (const [orgId, name] of [...pendingOrgs.current]) {
      if (!ms.some((m) => m.orgId === orgId)) continue;
      pendingOrgs.current.delete(orgId);
      if (fromInviteLink || ms.length === 1) setOrgIdRef.current(orgId);
      setJoiningName(null);
      toast(`Joined ${name}`, 'ok');
    }
  }, [toast]);

  const accept = useCallback(async (inv) => {
    if (!inv || !user?.uid) return false;
    if (inflight.current.has(inv.id)) return true;
    inflight.current.add(inv.id);
    setAcceptError(null);
    setJoiningName(inv.orgName || 'your organization');
    if (inv.orgId) pendingOrgs.current.set(inv.orgId, inv.orgName || 'the organization');
    try {
      await acceptInvite(inv, user);
      claimReady();
      return true;
    } catch (err) {
      inflight.current.delete(inv.id);
      if (inv.orgId) pendingOrgs.current.delete(inv.orgId);
      setJoiningName(null);
      const msg = err?.message || 'Could not accept the invitation';
      setAcceptError(msg);
      toast(msg, 'danger');
      return false;
    }
  }, [user, claimReady, toast]);

  const acceptRef = useRef(accept);
  acceptRef.current = accept;

  useEffect(() => {
    if (!user?.uid) return;
    for (const inv of invites) {
      if (!inv.acceptedByUid) acceptRef.current(inv);
    }
  }, [invites, user]);

  // The claims worker normally finishes in a few seconds. If it doesn't,
  // drop the "Joining…" screen instead of leaving the invitee there.
  useEffect(() => {
    if (!joiningName) return undefined;
    const t = setTimeout(() => {
      if (!pendingOrgs.current.size) return;
      pendingOrgs.current.clear();
      setJoiningName(null);
      setAcceptError('Joining is taking longer than expected. Refresh the page to try again.');
    }, 20000);
    return () => clearTimeout(t);
  }, [joiningName]);

  useEffect(() => { claimReady(); }, [memberships, claimReady]);

  const value = useMemo(
    () => ({ joiningName, acceptError, accept }),
    [joiningName, acceptError, accept]
  );
  return <InviteJoinContext.Provider value={value}>{children}</InviteJoinContext.Provider>;
}

export const useInviteJoin = () => useContext(InviteJoinContext);

// Current user's ID token for the backend AI/upload endpoints.
export async function idToken() {
  const user = firebase.auth().currentUser;
  return user ? user.getIdToken() : null;
}
